import { useState, useEffect } from 'react';

/**
 * Custom hook to handle signed URL images from Wasabi
 * @param {string} imageUrl - The image URL (can be signed URL endpoint or direct URL)
 * @param {string} fallbackUrl - Fallback image URL if loading fails
 * @returns {Object} - { imageUrl, isLoading, error }
 */
const useSignedImage = (imageUrl, fallbackUrl = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik01MCAzMEMzNS42NDE0IDMwIDI0IDQxLjY0MTQgMjQgNTZDMjQgNzAuMzU4NiAzNS42NDE0IDgyIDUwIDgyQzY0LjM1ODYgODIgNzYgNzAuMzU4NiA3NiA1NkM3NiA0MS42NDE0IDY0LjM1ODYgMzAgNTAgMzBaIiBmaWxsPSIjOUNBM0FGIi8+CjxwYXRoIGQ9Ik0yMCA4MEMyMCA3NS41ODE3IDIzLjU4MTcgNzIgMjggNzJINzJDNzYuNDE4MyA3MiA4MCA3NS41ODE3IDgwIDgwVjgySDIwVjgwWiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K') => {
  const [currentImageUrl, setCurrentImageUrl] = useState(imageUrl || fallbackUrl);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSignedUrl = async () => {
      
      // If no image URL provided, use fallback
      if (!imageUrl) {
        setCurrentImageUrl(fallbackUrl);
        return;
      }

      // If it's already a direct URL (not a signed URL endpoint), use it directly
      if ((imageUrl.includes('http') || imageUrl.startsWith('blob:')) && !imageUrl.includes('signed-url')) {
        // For blob URLs, we need to check if they're still valid
        if (imageUrl.startsWith('blob:')) {
          // Test if the blob URL is still valid by trying to load it
          const img = new Image();
          img.onload = () => {
            setCurrentImageUrl(imageUrl);
          };
          img.onerror = () => {
            setCurrentImageUrl(fallbackUrl);
          };
          img.src = imageUrl;
        } else {
          setCurrentImageUrl(imageUrl);
        }
        return;
      }

      // If it's a signed URL endpoint, fetch the actual signed URL
      if (imageUrl.includes('signed-url')) {
        setIsLoading(true);
        setError(null);
        
        try {
          const response = await fetch(imageUrl, {
            credentials: 'include',
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.url) {
              setCurrentImageUrl(data.url);
            } else {
              setCurrentImageUrl(fallbackUrl);
              setError('Failed to get signed URL');
            }
          } else {
            setCurrentImageUrl(fallbackUrl);
            setError(`HTTP ${response.status}`);
          }
        } catch (error) {
          setCurrentImageUrl(fallbackUrl);
          setError(error.message);
        } finally {
          setIsLoading(false);
        }
      } else {
        setCurrentImageUrl(fallbackUrl);
      }
    };

    fetchSignedUrl();
  }, [imageUrl, fallbackUrl]);

  return {
    imageUrl: currentImageUrl,
    isLoading,
    error
  };
};

export default useSignedImage;
